import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FileOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Flex, Form, Input, message, Modal, Radio, Space, Tooltip } from 'antd';
import { SelectProps } from 'antd/lib';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import CompanyLinkInfo from '@/components/general/CompanyLinkInfo';
import EditableModal, { useEditableModal } from '@/components/general/EditableModal';
import LinkTextField from '@/components/general/LinkTextField';
import {
  EditableInfoItem,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import UploadedFileList from '@/components/general/UploadFile/UploadedFileList';
import UploadFileComponent from '@/components/general/UploadFile/UploadFile';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import FileData from '@/model/General/FileData';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';
import { downloadFile } from '@/utils/downloadFile';

import { getReversedLogisticFileCode } from './receptionReversedLogisticFileUtils';

interface ReceptionShowDetailProps {
  record?: Reception;
  ticket?: any;
  refetch?: () => void;
}

const ReceptionShowDetail = (props: ReceptionShowDetailProps) => {
  const { t } = useTranslation();
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const { receptionRepository, packageRepository } = useRepositoriesContext();
  const { record, ticket, refetch } = props;
  const { activeModal, openModal, closeModal } = useEditableModal<
    'tracking' | 'shipper' | 'reversed_logistic'
  >();
  const [updatingPickUp, setUpdatingPickUp] = useState(false);
  // --- Estado para archivo de logística inversa ---
  const [reversedLogisticUploadFiles, setReversedLogisticUploadFiles] = useState<FileData[]>([]);
  const [reversedLogisticUploadModalOpen, setReversedLogisticUploadModalOpen] = useState(false);
  const { shippersSelectProps } = useOutletContext<{
    shippersSelectProps: SelectProps;
  }>();
  /** Extrae el nombre real del archivo desde una URL, descartando query params */
  const getFileNameFromUrl = (url: string): string =>
    decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'archivo-logistica-inversa');
  /**
   * Se hidratan los archivos de logística inversa existentes desde record.photos
   * filtrando por el código de descripción reversed_logistic_file_{id}.
   */
  const existingReversedLogisticFiles = useMemo(() => {
    if (!record?.id || !record?.photos) return [];
    const code = getReversedLogisticFileCode(record.id);

    return record.photos
      .filter(p => p.description === code)
      .map(
        f =>
          new FileData({
            id: f.id,
            title: getFileNameFromUrl(f.photo),
            file: f.photo,
          }),
      );
  }, [record?.id, record?.photos]);
  /**
   * Determina si se permite cargar archivos de logística inversa.
   * Solo logística puede subir, en estado PENDIENTE con reversed_logistic activo.
   */
  const canUploadReversedLogisticFile =
    hasPermission('edit') &&
    userWorkArea === USER_WORK_AREA.LOGISTICS &&
    record?.status === PACKAGE_STATES.PENDIENTE &&
    record?.reversed_logistic === true;
  /**
   * Metadata: handler de eliminación de archivo de logística inversa.
   * Marca el archivo como removido y lo elimina vía packageRepository.saveFiles.
   */
  const handleDeleteReversedLogisticFile = useCallback(
    (file: FileData) => {
      Modal.confirm({
        title: t('¿Estás seguro de que deseas eliminar este archivo de logística inversa?'),
        content: t('Esta acción no se puede deshacer'),
        okText: t('Sí, eliminar'),
        okType: 'danger',
        cancelText: t('No'),
        onOk: () => {
          if (!record?.id) return;
          const fileToRemove = new FileData({ id: file.id, title: file.title, file: file.fileUrl });

          fileToRemove.setRemoved(true);
          void packageRepository.saveFiles([fileToRemove], record.id).then(() => {
            void refetch?.();
          });
        },
      });
    },
    [t, record?.id, packageRepository, refetch],
  );

  return (
    <ShowInfoGrid>
      <ShowInfoItem label={t('Empresa o Técnico que envía el paquete')}>
        <CompanyLinkInfo company={record?.company} />
      </ShowInfoItem>

      <EditableInfoItem
        label={t('Operador logístico')}
        action={
          hasPermission('edit') &&
          record?.status == PACKAGE_STATES.PENDIENTE && (
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                openModal('shipper');
              }}
            />
          )
        }>
        <TextField value={record?.shipper?.name ?? '-'} />
      </EditableInfoItem>

      <EditableInfoItem
        label={t('Número de seguimiento')}
        action={
          hasPermission('edit') &&
          record?.status != PACKAGE_STATES.FINALIZADO &&
          record?.status != PACKAGE_STATES.CANCELADO && (
            <Button icon={<EditOutlined />} size="small" onClick={() => openModal('tracking')} />
          )
        }>
        <Flex vertical>
          <TextField value={record?.tracking_number ?? '-'} />
          {record?.tracking_number && record?.shipper?.tracking_link && (
            <LinkTextField
              to={`${record?.shipper?.tracking_link}/${record?.tracking_number}`}
              label={t('Ver seguimiento')}
              external
            />
          )}
        </Flex>
      </EditableInfoItem>

      <ShowInfoItem label={t('Retiro en deposito')}>
        {hasPermission('edit') &&
        (record?.status == PACKAGE_STATES.PENDIENTE ||
          record?.status == PACKAGE_STATES.EN_VIAJE) ? (
          <Radio.Group
            value={
              record?.has_to_be_picked_up == undefined ? undefined : record?.has_to_be_picked_up
            }
            onChange={e => {
              setUpdatingPickUp(true);
              void receptionRepository
                .updateReception(record.id, {
                  has_to_be_picked_up: e.target.value,
                })
                .then(() => {
                  props.refetch?.();
                  setUpdatingPickUp(false);
                });
            }}
            disabled={updatingPickUp}>
            <Radio value={true}>{t('Sí')}</Radio>
            <Radio value={false}>{t('No')}</Radio>
          </Radio.Group>
        ) : (
          <TextField value={record?.has_to_be_picked_up ? t('Sí') : t('No')} />
        )}
      </ShowInfoItem>

      {record?.ticket && ticket && (
        <ShowInfoItem label={t('Ticket asociado')}>
          <Flex vertical>
            <LinkTextField
              label={`${ticket?.ticket_title_str} (${ticket?.id})`}
              to={`/tickets/show/${ticket?.id}`}
              local
            />
            <LinkTextField
              label={t('Ver caso completo')}
              to={`/${RESOURCES.CASE_RESOLUTION}/${ticket?.id}`}
              local
            />
          </Flex>
        </ShowInfoItem>
      )}

      <ShowInfoItem label={t('Proceso de logística inversa')}>
        {hasPermission('edit') && record?.status == PACKAGE_STATES.PENDIENTE ? (
          <Radio.Group
            value={record?.reversed_logistic == undefined ? undefined : record?.reversed_logistic}
            onChange={e => {
              setUpdatingPickUp(true);
              void receptionRepository
                .updateReception(record.id, {
                  reversed_logistic: e.target.value,
                })
                .then(() => {
                  props.refetch?.();
                  setUpdatingPickUp(false);
                });
            }}
            disabled={updatingPickUp}>
            <Radio value={true}>{t('Sí')}</Radio>
            <Radio value={false}>{t('No')}</Radio>
          </Radio.Group>
        ) : (
          <TextField value={record?.reversed_logistic ? t('Sí') : t('No')} />
        )}
      </ShowInfoItem>

      <EditableInfoItem
        label={t('Código de logística inversa')}
        action={
          hasPermission('edit') &&
          userWorkArea == USER_WORK_AREA.LOGISTICS &&
          record?.status == PACKAGE_STATES.PENDIENTE && (
            <Flex gap={4}>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => {
                  openModal('reversed_logistic');
                }}
              />
              {canUploadReversedLogisticFile && (
                <Button
                  icon={<UploadOutlined />}
                  size="small"
                  disabled={existingReversedLogisticFiles.length > 0}
                  onClick={() => setReversedLogisticUploadModalOpen(true)}
                />
              )}
            </Flex>
          )
        }>
        <Flex vertical gap={8}>
          <TextField value={record?.reversed_logistic_code ?? t('No hay código registrado')} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {existingReversedLogisticFiles.length > 0 &&
              existingReversedLogisticFiles.map(file => (
                <Space key={file.id} size="small" align="center">
                  <FileOutlined />
                  <a
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: 'break-all' }}>
                    {file.title}
                  </a>
                  <Tooltip title={t('Descargar')}>
                    <Button
                      icon={<DownloadOutlined />}
                      size="small"
                      type="text"
                      onClick={() => void downloadFile(file)}
                    />
                  </Tooltip>
                  {canUploadReversedLogisticFile && (
                    <Tooltip title={t('Eliminar')}>
                      <Button
                        icon={<DeleteOutlined />}
                        size="small"
                        type="text"
                        danger
                        onClick={() => handleDeleteReversedLogisticFile(file)}
                      />
                    </Tooltip>
                  )}
                </Space>
              ))}
          </div>
          {canUploadReversedLogisticFile && (
            <Modal
              title={t('Cargar archivo de logística inversa')}
              open={reversedLogisticUploadModalOpen}
              onCancel={() => {
                setReversedLogisticUploadModalOpen(false);
                setReversedLogisticUploadFiles([]);
              }}
              okText={t('Cargar')}
              cancelText={t('Cancelar')}
              okButtonProps={{ disabled: reversedLogisticUploadFiles.length === 0 }}
              onOk={() => {
                Modal.confirm({
                  title: t('¿Estás seguro de que deseas cargar este archivo de logística inversa?'),
                  content: t('Esta acción no se puede deshacer'),
                  okText: t('Sí'),
                  okType: 'primary',
                  cancelText: t('No'),
                  onOk: () => {
                    if (!record?.id) {
                      setReversedLogisticUploadModalOpen(false);
                      setReversedLogisticUploadFiles([]);

                      return;
                    }

                    const fileCode = getReversedLogisticFileCode(record.id);
                    // Limitar a un solo archivo de logística inversa
                    const filesToUpload = reversedLogisticUploadFiles.slice(0, 1);

                    void packageRepository
                      .saveFiles(filesToUpload, record.id, fileCode)
                      .then(() => {
                        setReversedLogisticUploadFiles([]);
                        setReversedLogisticUploadModalOpen(false);
                        void refetch?.();
                      });
                  },
                });
              }}>
              <UploadFileComponent
                fileList={reversedLogisticUploadFiles}
                setFileList={(updater: any) => {
                  // Limitar a máximo 1 archivo de logística inversa
                  setReversedLogisticUploadFiles(prev => {
                    const result = typeof updater === 'function' ? updater(prev) : updater;

                    if (result.length > 1) {
                      message.warning(t('Solo se permite un archivo de logística inversa'));

                      return result.slice(-1);
                    }

                    return result;
                  });
                }}
              />
              {reversedLogisticUploadFiles.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <UploadedFileList
                    fileList={reversedLogisticUploadFiles}
                    setFileList={setReversedLogisticUploadFiles}
                  />
                </div>
              )}
            </Modal>
          )}
        </Flex>
      </EditableInfoItem>

      <EditableModal
        activeModal={activeModal}
        closeModal={closeModal}
        modalConfigByKey={{
          tracking: {
            title: t('Editar número de seguimiento'),
            form: {
              initialValues: { trackingNumber: record?.tracking_number },
              onFinish: async values => {
                if (
                  !record ||
                  values.trackingNumber === undefined ||
                  values.trackingNumber === record.tracking_number
                ) {
                  return false;
                }

                await receptionRepository.updateReception(record.id, {
                  tracking_number: values.trackingNumber,
                });
                refetch?.();
              },
              children: (
                <Form.Item name="trackingNumber">
                  <Input />
                </Form.Item>
              ),
            },
          },
          shipper: {
            title: t('Editar operador logístico'),
            form: {
              initialValues: { shipperId: record?.shipper?.id },
              onFinish: async values => {
                if (!record || values.shipperId === undefined) return false;

                await receptionRepository.updateReceptionShipper(record.id, values.shipperId);
                refetch?.();
              },
              children: (
                <Form.Item label={t('Operador logístico')} required={true} name="shipperId">
                  <AntdSelect {...shippersSelectProps} allowClear showSearch />
                </Form.Item>
              ),
            },
          },
          reversed_logistic: {
            title: t('Editar código de logística inversa'),
            form: {
              initialValues: { reversedLogisticCode: record?.reversed_logistic_code },
              onFinish: async values => {
                if (
                  !record ||
                  values.reversedLogisticCode === undefined ||
                  values.reversedLogisticCode === record.reversed_logistic_code
                ) {
                  return false;
                }

                await receptionRepository.updateReception(record.id, {
                  reversed_logistic_code: values.reversedLogisticCode,
                });
                refetch?.();
              },
              children: (
                <Form.Item name="reversedLogisticCode">
                  <Input allowClear />
                </Form.Item>
              ),
            },
          },
        }}
      />
    </ShowInfoGrid>
  );
};

export default ReceptionShowDetail;
